import { useState, type ReactNode } from "react";
import { Navigate, useSearchParams } from "react-router-dom";

import { useAuthContext } from "../auth/AuthContext";
import { friendlyAuthError } from "../auth/authErrors";
import type {
  AuthProviderConfig,
  AuthProviderId,
  B2cConfig,
} from "../auth/authProviders";
import { isEmbeddedInExtension } from "../bridge/types";
import { useExtSignInPopup } from "../auth/useExtSignInPopup";
import { useDarkMode } from "../theme/useDarkMode";
import B2cForm from "./login/B2cForm";
import SecondaryLoginModal from "./login/SecondaryLoginModal";
import SecondaryTiles from "./login/SecondaryTiles";
import { MicrosoftGlyph, ProviderIcon, Spinner } from "./login/icons";

/**
 * Atlas Copilot sign-in.
 *
 * Shaped to match Atlas Desktop (Provider Portal): the Atlas account
 * email/password form IS the card, and the other enabled sign-in methods sit
 * below it as secondary tiles that open an advisory modal. Providers
 * repeatedly mistook the old three-way picker — which gave "Astrana SSO",
 * "Pathways account" and "Atlas account" equal weight — for a choice they
 * were meant to make, when nearly all of them want the Atlas credentials they
 * already use on Atlas Desktop.
 *
 * "Atlas account" is the `provider-portal-b2c` provider: the same B2C tenant
 * Atlas Desktop authenticates against, which is what makes the parity real
 * rather than cosmetic.
 *
 * If that provider is NOT available — its feature flag is off, or its
 * `VITE_PROVIDER_PORTAL_B2C_*` env vars are unpopulated, in which case
 * `loadEnabledProviders()` drops it — there is no primary form to render, so
 * the screen falls back to the original picker rather than showing an empty
 * card.
 */
export default function LoginPage() {
  const auth = useAuthContext();
  const [searchParams] = useSearchParams();
  // Set by SessionGuard when it redirects after expired session.
  const sessionExpired = searchParams.get("reason") === "expired";

  if (auth.isAuthenticated) {
    return <Navigate to="/members" replace />;
  }

  const atlas = auth.enabledProviders.find(
    (p): p is B2cConfig => p.id === "provider-portal-b2c",
  );
  const secondaries = auth.enabledProviders.filter(
    (p) => p.id !== "provider-portal-b2c",
  );

  if (!atlas) {
    return <LegacyPickerLogin sessionExpired={sessionExpired} />;
  }

  return (
    <AtlasPrimaryLogin
      atlas={atlas}
      secondaries={secondaries}
      sessionExpired={sessionExpired}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Primary layout — Atlas form + secondary tiles                       */
/* ------------------------------------------------------------------ */

function AtlasPrimaryLogin({
  atlas,
  secondaries,
  sessionExpired,
}: {
  atlas: B2cConfig;
  secondaries: AuthProviderConfig[];
  sessionExpired: boolean;
}) {
  const auth = useAuthContext();
  const [modalProvider, setModalProvider] =
    useState<AuthProviderConfig | null>(null);
  const embedded = isEmbeddedInExtension();
  const popup = useExtSignInPopup((_id, tokens) =>
    auth.completeB2cSignIn(atlas.id, tokens),
  );

  return (
    <Shell
      sessionExpired={sessionExpired}
      below={
        <SecondaryTiles providers={secondaries} onPick={setModalProvider} />
      }
    >
      {embedded && !popup.blocked ? (
        <PopupSignInButton pending={popup.pending} onClick={popup.start} />
      ) : (
        <>
          {embedded && popup.blocked && (
            <p
              role="status"
              className="w-full rounded-lg border border-border bg-muted/60 px-3 py-2 text-sm text-muted-foreground text-center m-0"
            >
              Your browser blocked the sign-in pop-up. Sign in here instead
              (password autofill may be unavailable in the side panel).
            </p>
          )}
          <B2cForm
            variant="primary"
            onSubmit={(email, password) =>
              auth.loginWithB2c(atlas.id, email, password)
            }
          />
        </>
      )}

      {modalProvider && (
        <SecondaryLoginModal
          provider={modalProvider}
          onClose={() => setModalProvider(null)}
          onEntra={() => auth.loginWithEntra()}
          onB2c={(email, password) =>
            auth.loginWithB2c(
              modalProvider.id as "pathways-b2c" | "provider-portal-b2c",
              email,
              password,
            )
          }
        />
      )}
    </Shell>
  );
}

/**
 * Side-panel sign-in entry. The credential form itself opens in a top-level
 * window (see auth/extSignInHandoff.ts) because password managers cannot fill
 * or save inside the extension's iframe.
 */
function PopupSignInButton({
  pending,
  onClick,
}: {
  pending: boolean;
  onClick: () => void;
}) {
  return (
    <div className="w-full flex flex-col gap-3">
      <button
        type="button"
        onClick={onClick}
        disabled={pending}
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white bg-gradient-to-br from-[#8a1558] to-[#c42a83] hover:brightness-110 text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        {pending ? "Waiting for sign-in window…" : "Sign in"}
      </button>
      <p className="text-xs text-muted-foreground text-center m-0">
        Opens a secure window so your password manager can fill your Atlas
        account.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Fallback layout — the original picker                               */
/* ------------------------------------------------------------------ */

type Active =
  | { kind: "picker" }
  | { kind: "entra" }
  | { kind: "b2c"; provider: B2cConfig };

/**
 * The pre-parity screen, kept intact for environments where the Atlas
 * provider cannot be resolved. Unreachable wherever
 * `VITE_FEATURE_LOGIN_PROVIDER_PORTAL` is on and its config is populated.
 */
function LegacyPickerLogin({ sessionExpired }: { sessionExpired: boolean }) {
  const auth = useAuthContext();
  const [active, setActive] = useState<Active>({ kind: "picker" });
  const [error, setError] = useState<string | null>(null);

  async function runEntra() {
    setError(null);
    try {
      await auth.loginWithEntra();
    } catch (e) {
      setError(friendlyAuthError(e, "Sign in failed. Please try again."));
      setActive({ kind: "picker" });
    }
  }

  function pick(provider: AuthProviderConfig) {
    setError(null);
    if (provider.id === "entra") {
      setActive({ kind: "entra" });
      void runEntra();
      return;
    }
    setActive({ kind: "b2c", provider });
  }

  return (
    <Shell sessionExpired={sessionExpired}>
      {active.kind === "picker" && (
        <Picker providers={auth.enabledProviders} onPick={pick} />
      )}

      {active.kind === "entra" && (
        <div className="w-full flex flex-col items-center gap-3">
          <Spinner />
          <p className="text-sm text-muted-foreground m-0">
            Redirecting you to sign in…
          </p>
          <button
            type="button"
            onClick={() => setActive({ kind: "picker" })}
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Choose a different method
          </button>
        </div>
      )}

      {active.kind === "b2c" && (
        <>
          {/* Which provider the credentials are for — the picker is the only
              place the user can land here, so the label is the only thing
              distinguishing an Atlas form from a Pathways one. */}
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground m-0">
              {active.provider.label}
            </p>
          </div>
          <B2cForm
            variant="secondary"
            onSubmit={(email, password) =>
              auth.loginWithB2c(
                active.provider.id as "pathways-b2c" | "provider-portal-b2c",
                email,
                password,
              )
            }
            footer={
              auth.enabledProviders.length > 1 ? (
                <button
                  type="button"
                  onClick={() => setActive({ kind: "picker" })}
                  className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  ← Choose a different method
                </button>
              ) : undefined
            }
          />
        </>
      )}

      {error && (
        <p
          role="alert"
          className="text-sm text-destructive font-medium m-0 text-center"
        >
          {error}
        </p>
      )}
    </Shell>
  );
}

function Picker({
  providers,
  onPick,
}: {
  providers: AuthProviderConfig[];
  onPick: (p: AuthProviderConfig) => void;
}) {
  if (providers.length === 0) {
    return (
      <div className="text-center">
        <p className="text-sm font-semibold text-foreground m-0">
          No sign-in methods configured.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-2.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground text-center">
        Sign in with
      </p>
      {providers.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onPick(p)}
          className={providerButtonClass(p.id)}
        >
          {p.id === "entra" ? <MicrosoftGlyph /> : <ProviderIcon id={p.id} />}
          <span>{p.label}</span>
        </button>
      ))}
    </div>
  );
}

function providerButtonClass(id: AuthProviderId): string {
  // Entra gets the brand gradient CTA; B2C providers get a softer secondary
  // treatment so the two don't fight each other for attention.
  const common =
    "w-full inline-flex items-center justify-center gap-3 px-5 py-3 rounded-xl text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background";
  if (id === "entra") {
    return (
      common +
      " text-white bg-gradient-to-br from-[#8a1558] to-[#c42a83] hover:brightness-110 hover:shadow-[0_4px_14px_rgba(138,21,88,0.3)] focus-visible:ring-ring"
    );
  }
  return (
    common +
    " bg-card border border-border text-foreground hover:border-primary hover:text-primary focus-visible:ring-ring"
  );
}

/* ------------------------------------------------------------------ */
/* Shared chrome                                                       */
/* ------------------------------------------------------------------ */

function Shell({
  children,
  below,
  sessionExpired,
}: {
  children: ReactNode;
  below?: ReactNode;
  sessionExpired: boolean;
}) {
  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center p-4 font-sans text-foreground relative bg-gradient-to-br from-gray-50 via-white to-gray-100/60 dark:from-background dark:via-background dark:to-background">
      {/*
        Card shadow mirrors Provider Portal's login card — a stacked
        triple-shadow with a plum tint on the outer rings. Gives the card
        visible lift against the soft background-gradient without the harsh
        black drop-shadow the default `shadow-xl` produces.
      */}
      <div className="w-full max-w-sm bg-gradient-to-br from-white via-white/95 to-gray-100/70 dark:from-card dark:via-card dark:to-card text-card-foreground rounded-2xl border border-border/60 p-8 sm:p-10 flex flex-col items-center gap-6 shadow-[0_24px_50px_-20px_rgba(0,0,0,0.22),_0_0_40px_-12px_rgba(74,8,48,0.06),_0_-3px_24px_-10px_rgba(138,21,88,0.04)]">
        <Brand />

        {sessionExpired && (
          <div
            role="status"
            className="w-full rounded-lg border border-border bg-muted/60 px-3 py-2 text-sm text-muted-foreground text-center"
          >
            Your session timed out. Sign in again to continue.
          </div>
        )}

        <div className="w-full border-t border-border" />

        {children}
      </div>

      {below}

      <LoginFooter />
      <BuildBadge />
    </div>
  );
}

function Brand() {
  const { isDark } = useDarkMode();
  const symbol = isDark
    ? "/images/logo_A_white.svg"
    : "/images/logo_A_plum.svg";
  return (
    <div className="flex flex-row items-center justify-center gap-3">
      <div
        style={{
          width: 40,
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 9999,
          background: isDark
            ? "linear-gradient(to bottom, rgba(198, 107, 162, 0.25), transparent)"
            : "linear-gradient(to bottom, rgba(108, 12, 70, 0.12), transparent)",
        }}
      >
        <img src={symbol} alt="Astrana Health Logo" style={{ width: 20 }} />
      </div>
      {/*
        Gradient text matches Provider Portal's onboarding login — plum ramp,
        clipped to the glyph.
      */}
      <h1 className="text-2xl font-bold m-0 bg-gradient-to-r from-primary to-[hsl(324,60%,45%)] dark:from-[hsl(324,60%,70%)] dark:to-[hsl(324,80%,82%)] bg-clip-text text-transparent">
        Atlas Copilot
      </h1>
    </div>
  );
}

// URLs are the live Astrana notices, same PDFs Provider Portal's onboarding
// login uses. Intentionally omit PP's Help Center link — that Notion page is
// Provider-Portal-specific content.
const PRIVACY_URL =
  "https://www.astranacare.com/wp-content/uploads/2024/06/NOTICE-OF-PRIVACY-PRACTICES_ASTRANA-CARE.pdf";
const NON_DISCRIMINATION_URL =
  "https://www.astranahealthmanagement.com/wp-content/uploads/2025/05/notice-non-discrimination-astrana.pdf";

// Two-row footer: legal links on one line, copyright on a quiet second line
// underneath. Puts the interactive items together and de-emphasises the
// copyright, which is only there for compliance.
function LoginFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="mt-6 flex flex-col items-center gap-1.5">
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <a
          href={PRIVACY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-foreground transition-colors"
        >
          Privacy
        </a>
        <span aria-hidden="true">·</span>
        <a
          href={NON_DISCRIMINATION_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-foreground transition-colors"
        >
          Non-Discrimination
        </a>
      </div>
      <div className="text-[11px] text-muted-foreground/70">
        © {year} Astrana Health
      </div>
    </footer>
  );
}

function BuildBadge() {
  // `VITE_APP_VERSION` is stamped in CI. When running locally without the var
  // set, render nothing — "Build: dev" was noise.
  const version = import.meta.env.VITE_APP_VERSION;
  if (!version) return null;
  return (
    <div className="absolute bottom-2 right-3 text-[11px] text-muted-foreground/60 font-mono">
      Build: {version}
    </div>
  );
}
